import axios, { AxiosError, AxiosResponse } from 'axios';
import {
  PublicTopic,
  PostCard,
  PublicPost,
  PostSearchResult,
  BlogSearchFilters,
  BlogSearchQuery,
  TopicsResponse,
  PostsResponse,
  FeaturedPostsResponse,
  PostDetailResponse,
  SearchPostsResponse,
  TopicPostsResponse,
  PaginationInfo
} from '../types/blog';

// Retry configuration
const RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second base delay

// Create axios instance with default config
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Utility function for exponential backoff retry
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

async function retryRequest<T>(
  requestFn: () => Promise<AxiosResponse<T>>,
  attempts: number = RETRY_ATTEMPTS
): Promise<AxiosResponse<T>> {
  for (let i = 0; i < attempts; i++) {
    try {
      return await requestFn();
    } catch (error) {
      const isLastAttempt = i === attempts - 1;
      const isRetryableError = error instanceof AxiosError && 
        (error.code === 'NETWORK_ERROR' || 
         error.code === 'ECONNABORTED' ||
         (error.response?.status && error.response.status >= 500));

      if (isLastAttempt || !isRetryableError) {
        throw error;
      }

      // Exponential backoff: wait longer between each retry
      const delayMs = RETRY_DELAY * Math.pow(2, i);
      console.log(`Request failed (attempt ${i + 1}/${attempts}), retrying in ${delayMs}ms...`);
      await sleep(delayMs);
    }
  }
  
  throw new Error('Max retry attempts exceeded');
}

// Enhanced user-friendly error messages
function getUserFriendlyErrorMessage(error: AxiosError): string {
  if (!error.response) {
    if (error.code === 'NETWORK_ERROR') {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. The server is taking too long to respond. Please try again.';
    }
    return 'Network error. Please check your connection and try again.';
  }
  
  const status = error.response.status;
  const data = error.response.data as any;
  
  switch (status) {
    case 400:
      return data?.message || 'Invalid request. Please check your input and try again.';
    case 401:
      return 'Authentication required. Please sign in to continue.';
    case 403:
      return 'Access denied. You don\'t have permission to access this resource.';
    case 404:
      return data?.message || 'The requested content was not found.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'Server error. Our team has been notified. Please try again later.';
    case 502:
      return 'Service temporarily unavailable. Please try again in a few minutes.';
    case 503:
      return 'Service maintenance in progress. Please try again later.';
    default:
      return data?.message || `An unexpected error occurred (${status}). Please try again.`;
  }
}

// Add request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`Blog API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Blog API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    console.error('Blog API Response Error:', error);
    
    const userMessage = getUserFriendlyErrorMessage(error);
    throw new Error(userMessage);
  }
);

export interface PaginatedPostsResponse {
  posts: PostCard[];
  pagination: PaginationInfo;
}

export interface PaginatedSearchResponse {
  results: PostSearchResult[];
  pagination: PaginationInfo;
}

export class BlogApi {
  /**
   * Fetch all blog topics with article counts
   */
  static async getTopics(): Promise<PublicTopic[]> {
    try {
      const response = await retryRequest(
        () => api.get<TopicsResponse>('/topics')
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch topics');
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Error fetching topics:', error);
      throw error;
    }
  }

  /**
   * Fetch published posts with optional filters
   */
  static async getPosts(filters: BlogSearchFilters = {}): Promise<PaginatedPostsResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) {
        params.append('search', filters.search);
      }
      
      if (filters.topic_id) {
        params.append('topic_id', filters.topic_id.toString());
      }
      
      if (filters.sort) {
        params.append('sort', filters.sort);
      }
      
      if (filters.page) {
        params.append('page', filters.page.toString());
      }
      
      if (filters.limit) {
        params.append('limit', filters.limit.toString());
      }
      
      const response = await retryRequest(
        () => api.get<PostsResponse>(`/posts?${params.toString()}`)
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch posts');
      }
      
      const posts = response.data.data;
      const pagination = response.data.pagination;
      
      return {
        posts,
        pagination: {
          currentPage: pagination?.page || 1,
          totalPages: pagination?.totalPages || 1,
          totalItems: pagination?.total || posts.length,
          hasNextPage: (pagination?.page || 1) < (pagination?.totalPages || 1),
          hasPrevPage: (pagination?.page || 1) > 1,
          itemsPerPage: pagination?.limit || 10,
        }
      };
    } catch (error) {
      console.error('Error fetching posts:', error);
      throw error;
    }
  }

  /**
   * Fetch featured posts (top 5 most viewed)
   */
  static async getFeaturedPosts(): Promise<PostCard[]> {
    try {
      const response = await retryRequest(
        () => api.get<FeaturedPostsResponse>('/posts/featured')
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch featured posts');
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Error fetching featured posts:', error);
      throw error;
    }
  }

  /**
   * Search posts with highlighted results
   */
  static async searchPosts(query: BlogSearchQuery): Promise<PaginatedSearchResponse> {
    try {
      const params = new URLSearchParams();
      params.append('q', query.q);
      
      if (query.topic_id) {
        params.append('topic_id', query.topic_id.toString());
      }
      
      if (query.page) {
        params.append('page', query.page.toString());
      }
      
      if (query.limit) {
        params.append('limit', query.limit.toString());
      }
      
      const response = await retryRequest(
        () => api.get<SearchPostsResponse>(`/posts/search?${params.toString()}`)
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to search posts');
      }
      
      const results = response.data.data;
      const pagination = response.data.pagination;
      
      return {
        results,
        pagination: {
          currentPage: pagination?.page || 1,
          totalPages: pagination?.totalPages || 1,
          totalItems: pagination?.total || results.length,
          hasNextPage: (pagination?.page || 1) < (pagination?.totalPages || 1),
          hasPrevPage: (pagination?.page || 1) > 1,
          itemsPerPage: pagination?.limit || 10,
        }
      };
    } catch (error) {
      console.error('Error searching posts:', error);
      throw error;
    }
  }

  /**
   * Fetch a single post by UUID and increment view counter
   */
  static async getPost(uuid: string): Promise<PublicPost> {
    try {
      const response = await retryRequest(
        () => api.get<PostDetailResponse>(`/posts/${uuid}`)
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch post');
      }
      
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching post ${uuid}:`, error);
      throw error;
    }
  }

  /**
   * Fetch topic details by slug
   */
  static async getTopicBySlug(topicSlug: string): Promise<PublicTopic> {
    try {
      const response = await retryRequest(
        () => api.get<{ success: boolean; message: string; data: PublicTopic }>(`/topics/${topicSlug}`)
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch topic details');
      }
      
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching topic ${topicSlug}:`, error);
      throw error;
    }
  }

  /**
   * Fetch posts by topic slug
   */
  static async getPostsByTopic(
    topicSlug: string, 
    filters: Omit<BlogSearchFilters, 'topic_id'> = {}
  ): Promise<PaginatedPostsResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.sort) {
        params.append('sort', filters.sort);
      }
      
      if (filters.page) {
        params.append('page', filters.page.toString());
      }
      
      if (filters.limit) {
        params.append('limit', filters.limit.toString());
      }
      
      const response = await retryRequest(
        () => api.get<TopicPostsResponse>(`/topics/${topicSlug}/posts?${params.toString()}`)
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch topic posts');
      }
      
      const posts = response.data.data;
      const pagination = response.data.pagination;
      
      return {
        posts,
        pagination: {
          currentPage: pagination?.page || 1,
          totalPages: pagination?.totalPages || 1,
          totalItems: pagination?.total || posts.length,
          hasNextPage: (pagination?.page || 1) < (pagination?.totalPages || 1),
          hasPrevPage: (pagination?.page || 1) > 1,
          itemsPerPage: pagination?.limit || 10,
        }
      };
    } catch (error) {
      console.error(`Error fetching posts for topic ${topicSlug}:`, error);
      throw error;
    }
  }

  /**
   * Like a post
   */
  static async likePost(uuid: string): Promise<{ totalLikes: number }> {
    try {
      const response = await retryRequest(
        () => api.post(`/posts/${uuid}/like`)
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to like post');
      }
      
      return response.data.data;
    } catch (error) {
      console.error(`Error liking post ${uuid}:`, error);
      throw error;
    }
  }

  /**
   * Share a post
   */
  static async sharePost(uuid: string, platform: 'facebook' | 'linkedin' | 'zalo'): Promise<{ totalShares: number }> {
    try {
      const response = await retryRequest(
        () => api.post(`/posts/${uuid}/share`, { platform })
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to record share');
      }
      
      return response.data.data;
    } catch (error) {
      console.error(`Error sharing post ${uuid}:`, error);
      throw error;
    }
  }

  /**
   * Validate blog API health
   */
  static async healthCheck(): Promise<boolean> {
    try {
      const response = await retryRequest(
        () => api.get('/topics') // Use topics endpoint as health check
      );
      return response.status === 200;
    } catch (error) {
      console.error('Blog API health check failed:', error);
      return false;
    }
  }
}

export default BlogApi;